import axios from 'axios'
// I (resilience): use the SHARED app-wide token-refresh single-flight instead of a per-module copy.
// Each module used to keep its OWN refreshPromise, so two modules refreshing in the same instant both
// presented the same ROTATING refresh token — the loser got a 401 and the user was spuriously logged
// out. The shared helper is one single-flight for the whole app and also applies the idle-timeout
// guard these copies skipped.
import { ensureFreshToken } from '../../../shared/api/tokenRefresh'
import { markApiActivity } from '../../../shared/api/sessionPolicy'
import { rejectWithUserMessage } from '../../../shared/api/failure'

const FINANCE_API_URL = import.meta.env.VITE_FINANCE_API_URL || '/api/finance'
const IDENTITY_URL = import.meta.env.VITE_IDENTITY_API_URL || '/api/identity'


const api = axios.create({
  baseURL: FINANCE_API_URL,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(async (config) => {
  const token = await ensureFreshToken()
  if (token) config.headers.Authorization = `Bearer ${token}`

  // A request that CHANGES something is somebody pressing something, and until now nothing
  // an API did counted as being here at all — a twenty-minute upload or a long report left a
  // person looking idle. Reads are deliberately excluded: this app runs background pollers,
  // and if a GET counted, a dashboard left open would hold a session open forever.
  markApiActivity(config)
  return config
})

// Was `(error) => Promise.reject(error)` — an interceptor that existed and did nothing, while
// reading to the next person as though error handling lived here. This module builds its own axios
// instance rather than using `createAPIClient`, so nothing was setting `userMessage` and every
// screen on this client fell back to a sentence written in the browser. It attaches the message and
// NOTHING else: no session clearing, no redirect. That belongs to the shared auth client alone.
api.interceptors.response.use((response) => response, rejectWithUserMessage)

// ---------- Invoice Packages ----------
export const invoicePackagesAPI = {
  list:             (params = {}) => api.get('/invoice-packages', { params }),
  get:              (id)          => api.get(`/invoice-packages/${id}`),
  dashboard:        (params = {}) => api.get('/invoice-packages/dashboard', { params }),
  availableTickets: (params = {}) => api.get('/invoice-packages/available-tickets', { params }),
  autoGenerate:     (data)        => api.post('/invoice-packages/auto-generate', data),
  manualBundle:     (data)        => api.post('/invoice-packages/manual-bundle', data),
  update:           (id, data)    => api.put(`/invoice-packages/${id}`, data),
  finalize:         (id, data={}) => api.post(`/invoice-packages/${id}/finalize`, data),
  send:             (id)          => api.post(`/invoice-packages/${id}/send`),
  emailClient:      (id, payload) => api.post(`/invoice-packages/${id}/email-client`, payload),
  pay:              (id)          => api.post(`/invoice-packages/${id}/pay`),
  void:             (id, reason)  => api.post(`/invoice-packages/${id}/void`, { reason }),
  getPdf:           (id)          => api.get(`/invoice-packages/${id}/pdf`, { responseType: 'blob' }),
  previewPdf:       (id)          => api.get(`/invoice-packages/${id}/pdf`, { params: { preview: true }, responseType: 'blob' }),
  getTicketDetails: (pkgId, ftId) => api.get(`/invoice-packages/${pkgId}/ticket-details/${ftId}`),
  saveOverrides:    (pkgId, data) => api.put(`/invoice-packages/${pkgId}/overrides`, data),
  // Signature override
  getSignatureOverride:        ()     => api.get('/invoice-packages/signature-override'),
  activateSignatureOverride:   (data) => api.post('/invoice-packages/signature-override', data),
  deactivateSignatureOverride: ()     => api.delete('/invoice-packages/signature-override'),
  deletePackage: async (packageId) => {
    const response = await api.delete(`/invoice-packages/${packageId}`)
    return response.data
  },

  // ---- RECEIPTS ----
  uploadReceipt: async (file, data) => {
    const formData = new FormData()
    formData.append('file', file)
    if (data.job_reference) formData.append('job_reference', data.job_reference)
    if (data.project_id) formData.append('project_id', data.project_id)
    if (data.vendor) formData.append('vendor', data.vendor)
    // The vendor PICKED from the company list (purchasing, C5): the server writes its name as `vendor`.
    if (data.vendor_id) formData.append('vendor_id', data.vendor_id)
    if (data.amount) formData.append('amount', data.amount)
    if (data.notes) formData.append('notes', data.notes)
    if (data.expense_category) formData.append('expense_category', data.expense_category)
    // The jobs a shared bill is split across, named on the upload form. Sent as JSON on the same
    // multipart post so the receipt and its split land in ONE transaction: a split that does not add
    // up refuses the upload outright rather than leaving an unsplit bill in the queue.
    if (data.split_lines?.length) formData.append('split_lines', JSON.stringify(data.split_lines))

    const response = await api.post('/receipts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  // ---- THE COMPANY VENDOR LIST (purchasing, C5) - picked on a receipt ----
  listVendors: async () => {
    const response = await api.get('/vendors')
    return response.data.vendors
  },
  addVendor: async (name) => {
    const response = await api.post('/vendors', { name })
    return response.data
  },

  getReceipts: async (status = null) => {
    const params = status ? { status } : {}
    const response = await api.get('/receipts/', { params })
    return response.data
  },

  updateReceiptStatus: async (receiptId, status, jobReference = null) => {
    const formData = new FormData()
    formData.append('status', status)
    if (jobReference) formData.append('job_reference', jobReference)
    
    const response = await api.patch(`/receipts/${receiptId}/status`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  updateReceipt: async (receiptId, fields) => {
    const formData = new FormData()
    Object.entries(fields).forEach(([k, v]) => {
      if (v === undefined || v === null) return
      // amount is typed `float` on the backend — an empty string 422s ("Failed to update").
      // Omit it so an amount-less receipt (or one whose amount was cleared) still saves.
      if (v === '' && k === 'amount') return
      formData.append(k, v)
    })
    const response = await api.patch(`/receipts/${receiptId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  // C3: the company's Fleet machines a FUEL receipt can be tagged to (number and type, no money): { machines }.
  getMachineChoices: async () => {
    const response = await api.get('/receipts/machine-choices')
    return response.data
  },

  getReceiptDownloadUrl: async (receiptId) => {
    const response = await api.get(`/receipts/${receiptId}/download`)
    return response.data.download_url
  },

  deleteReceipt: async (receiptId) => {
    const response = await api.delete(`/receipts/${receiptId}`)
    return response.data
  },

  // ---- A VENDOR'S RENTAL INVOICE (PLAN-30 P3, the owner's Option A) ----
  // The company's Fleet rentals to link an invoice to (machine, vendor, job, days on rent - no money), and its
  // "Rental invoices arrive" choice ('' | 'per_machine' | 'monthly_statement' | 'either'): { rentals, invoices_arrive }.
  getRentalChoices: async (search = '') => {
    const response = await api.get('/receipts/rental-choices', { params: { search } })
    return response.data || { rentals: [], invoices_arrive: '' }
  },
  // Link a PENDING Equipment receipt to the rental(s) it covers: [{ rental_id, project_id, cost_code, amount,
  // period_start, period_end }]. Two or more lines split it into parts that must add up to it to the cent; no
  // lines removes the link. Nothing posts here - the receipt (or each part) is approved as always.
  setRentalLines: async (receiptId, lines) => {
    const response = await api.put(`/receipts/${receiptId}/rental-lines`, { lines })
    return response.data
  },
  // Split a PENDING receipt of ANY category across several jobs: [{ project_id, cost_code, amount, note }].
  // Two or more lines, adding up to the bill to the cent; no lines removes the split. Nothing posts here -
  // each part is approved as always. The rental split above is the other kind, and a receipt has one or the
  // other, never both (services/expense_split_service.py).
  setExpenseSplit: async (receiptId, lines) => {
    const response = await api.put(`/receipts/${receiptId}/split-lines`, { lines })
    return response.data
  },

  // `idempotencyKey` identifies the ATTEMPT, and the caller owns it deliberately: the import
  // commits before it responds, so a proxy timeout shows a failure for an import that succeeded and
  // pressing the button again is the obvious next move. Re-using the key across those retries is
  // what lets the server recognise them as one action and hand back the original report instead of
  // importing again. Minting it here would defeat that — every retry would look like a new import.
  // `confirmRows` is the operator's answer to a report of held-back rows: yes, THESE really are
  // second charges. The server never decides that on its own, because identical bytes are all a
  // re-upload and a repeated charge have in common. It carries the `fingerprint` of each row the
  // report held back, and it has to — the flag it replaces was file-wide, so confirming two genuine
  // repeats also re-imported every row that had just landed successfully in the same request.
  importCSV: async (file, { idempotencyKey, confirmRows = [] } = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    if (idempotencyKey) formData.append('idempotency_key', idempotencyKey)
    if (confirmRows.length) formData.append('confirm_rows', confirmRows.join(','))
    const response = await api.post('/receipts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  getLedger: async () => {
    const response = await api.get('/receipts/ledger')
    return response.data
  },

  // Every group of receipts in this company that look like ONE piece of paper — including ones
  // already processed. Read-only, and the half that runs BACKWARDS: it reads receipts that are
  // already in the table, so "how often has this already happened to us" has an answer without
  // anybody having typed an invoice number or scanned anything.
  getDuplicateReceipts: async ({ windowDays, includeResolved } = {}) => {
    const params = {}
    if (windowDays) params.window_days = windowDays
    if (includeResolved) params.include_resolved = true
    const response = await api.get('/receipts/duplicates', { params })
    return response.data
  },

  // The operator's answer about one flagged pair. `verdict` is 'duplicate' or 'not_duplicate', and
  // for 'duplicate' `keepReceiptId` names which of the two survives — the other is retired to
  // 'rejected' and its ledger cost reversed through the same append-only path a mis-click on Reject
  // already uses. This is the ONLY receipts call that moves money on the strength of a duplicate
  // flag; detecting one never does.
  //
  // `windowDays` MUST be the window the operator was looking at. The server re-checks that the two
  // receipts still match before it records anything, and a re-check pinned to the default silently
  // refused every pair a wider report had surfaced — with a message claiming the receipts had been
  // edited or already resolved, which was not true. If the screen widened the question, the answer
  // has to carry the same question back.
  resolveDuplicateReceipts: async (receiptId, otherReceiptId, verdict,
    { keepReceiptId, note, windowDays } = {}) => {
    const formData = new FormData()
    formData.append('receipt_id', receiptId)
    formData.append('other_receipt_id', otherReceiptId)
    formData.append('verdict', verdict)
    if (keepReceiptId) formData.append('keep_receipt_id', keepReceiptId)
    if (note) formData.append('note', note)
    if (windowDays) formData.append('window_days', windowDays)
    const response = await api.post('/receipts/duplicates/resolve', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

export default api
